/**
 * ASCIN-native login / register page.
 *
 * Talks to /api/v1/ascin-auth (multi-tenant JWT). Distinct from the
 * Firebase SSO login so customers who run ASCIN standalone don't need
 * a Firebase project.
 */
import React, { useState } from 'react';
import {
  Box, Paper, Tabs, Tab, TextField, Button, Typography, Alert,
  Stack, Divider, CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ascinAuth } from '../../services/ascinAuthApi';

export default function AscinLoginPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<0 | 1>(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null); setInfo(null); setBusy(true);
    try {
      if (tab === 0) {
        const r = await ascinAuth.login(email.trim(), password);
        setInfo(`Welcome back, ${r.user.full_name || r.user.email}`);
      } else {
        if (password.length < 8) throw new Error('Password must be at least 8 characters');
        const r = await ascinAuth.register({
          email: email.trim(),
          password,
          full_name: fullName.trim() || undefined,
          tenant_name: tenantName.trim() || undefined,
        });
        setInfo(`Workspace created. Welcome, ${r.user.full_name || r.user.email}`);
      }
      // Brief pause so user sees the confirmation, then go to app
      setTimeout(() => navigate('/'), 400);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      let msg: string;
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) msg = detail.map((d: any) => d.msg || JSON.stringify(d)).join('; ');
      else msg = e?.message || 'Request failed';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', bgcolor: 'background.default', p: 2,
    }}>
      <Paper sx={{ p: 4, width: '100%', maxWidth: 440 }}>
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Typography variant="h4" fontWeight={600}>ASCIN</Typography>
          <Typography variant="body2" color="text.secondary">
            Adaptive Supply Chain Intelligence Net — multi-tenant workspace
          </Typography>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setErr(null); setInfo(null); }} sx={{ mb: 2 }}>
          <Tab label="Sign in" />
          <Tab label="Create workspace" />
        </Tabs>

        <form onSubmit={onSubmit}>
          <Stack spacing={2}>
            {tab === 1 && (
              <>
                <TextField
                  label="Your name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  fullWidth size="small"
                />
                <TextField
                  label="Workspace / company name"
                  value={tenantName}
                  onChange={(e) => setTenantName(e.target.value)}
                  fullWidth size="small"
                  helperText="A new tenant is created. You become its owner."
                />
              </>
            )}
            <TextField
              label="Email" type="email" required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth size="small"
              autoComplete="email"
            />
            <TextField
              label="Password" type="password" required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth size="small"
              inputProps={{ minLength: tab === 1 ? 8 : 0 }}
              helperText={tab === 1 ? 'Minimum 8 characters' : ' '}
              autoComplete={tab === 0 ? 'current-password' : 'new-password'}
            />

            {err && <Alert severity="error">{err}</Alert>}
            {info && <Alert severity="success">{info}</Alert>}

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={busy}
              startIcon={busy ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              {tab === 0 ? 'Sign in' : 'Create workspace'}
            </Button>

            <Typography variant="caption" color="text.secondary" align="center">
              Tenant data is isolated. Token stored locally for 7 days.
            </Typography>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
}
